import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogOverlay, DialogPortal } from "@radix-ui/react-dialog";
import { X, Heart, Bookmark, Share, IndianRupee, Play, Volume2, VolumeX, BookmarkCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserContext } from "@/context/AuthContext";
import { QUERY_KEYS, useGetSingleVideo, useGetVideoInteractions, useVideoInteractionManager } from "@/bizz/lib/react-query/bizzQueries";
import { useVideoTipPayment } from "@/bizz/lib/cashfree/videoTipPayment";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@/components/ui/use-toast";
import PaymentPhoneField from "@/components/shared/PaymentPhoneField";
import { paymentContactFromIdentifier, requiresPaymentPhone } from "@/lib/cashfree/paymentContact";

interface BizzVideoModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoId: string;
}

const BizzVideoModal = ({ isOpen, onClose, videoId }: BizzVideoModalProps) => {
  const { user } = useUserContext();
  const queryClient = useQueryClient();
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showTipModal, setShowTipModal] = useState(false);
  const [tipAmount, setTipAmount] = useState(10);
  const [paymentPhone, setPaymentPhone] = useState("");
  const videoRef = useRef<HTMLVideoElement>(null);

  // Data hooks

  const { data: videoData, isLoading } = useGetSingleVideo(videoId);
  const video = videoData as any;
  const { data: interactions } = useGetVideoInteractions(videoId, user?.id || "");
  
  // Interaction hooks
  const videoInteractionManager = useVideoInteractionManager(user?.id || "");
  const { handleTipPayment, isProcessing: isPaymentLoading } = useVideoTipPayment();

  // Local state for optimistic updates
  const [localInteractions, setLocalInteractions] = useState({
    isLiked: false,
    isSaved: false,
  });

  // Update local state when data loads
  useEffect(() => {
    if (interactions) {
      setLocalInteractions({
        isLiked: interactions.isLiked,
        isSaved: interactions.isSaved,
      });
    }
  }, [interactions]);

  // Auto-play when modal opens
  useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  }, [isOpen, video]);

  const togglePlayPause = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play();
        setIsPlaying(true);
      } else {
        videoRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  const handleVideoLike = async () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to like videos",
        variant: "destructive",
      });
      return;
    }

    try {
      // Optimistic update
      setLocalInteractions(prev => ({
        ...prev,
        isLiked: !prev.isLiked,
      }));

      await videoInteractionManager.handleLike(videoId);

      // Refresh data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_INTERACTIONS, videoId, user.id],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SINGLE_VIDEO, videoId],
      });
    } catch (error) {
      // Revert on error
      setLocalInteractions(prev => ({
        ...prev,
        isLiked: !prev.isLiked,
      }));
      console.error("Error liking video:", error);
    }
  };

  const handleVideoSave = async () => {
    if (!user?.id) {
      toast({
        title: "Login Required", 
        description: "Please login to save videos",
        variant: "destructive",
      });
      return;
    }

    try {
      // Optimistic update
      setLocalInteractions(prev => ({
        ...prev,
        isSaved: !prev.isSaved,
      }));

      await videoInteractionManager.handleSave(videoId);

      // Refresh data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_VIDEO_INTERACTIONS, videoId, user.id],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_SINGLE_VIDEO, videoId],
      });
    } catch (error) {
      // Revert on error
      setLocalInteractions(prev => ({
        ...prev,
        isSaved: !prev.isSaved,
      }));
      console.error("Error saving video:", error);
    }
  };

  const handleVideoShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: video?.title || "Check out this business video",
          url: `${window.location.origin}/bizz/video/${videoId}`,
        });
      } else {
        await navigator.clipboard.writeText(
          `${window.location.origin}/bizz/video/${videoId}`
        );
        toast({
          title: "Link Copied!",
          description: "Video link copied to clipboard",
        });
      }

      // Track share
      if (user?.id) {
        await videoInteractionManager.handleShare(videoId);
      }
    } catch (error) {
      console.error("Error sharing video:", error);
    }
  };

  const handleTipClick = () => {
    if (!user?.id) {
      toast({
        title: "Login Required",
        description: "Please login to send tips",
        variant: "destructive",
      });
      return;
    }
    setShowTipModal(true);
  };

  const handleSendTip = async () => {
    if (!video || !user?.id) return;

    try {
      await handleTipPayment(
        user.id,
        video.creator.$id,
        videoId,
        tipAmount,
        { 
          name: user.name, 
          emailOrPhone: user.emailOrPhone,
          paymentPhone,
        },
        () => {
          setShowTipModal(false);
          setTipAmount(10);
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_SINGLE_VIDEO, videoId],
          });
        }
      );
    } catch (error) {
      console.error("Error in tip payment:", error);
    }
  };

  if (isLoading || !video) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 bg-black/90 z-50" />
          <DialogContent className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="text-white">Loading video...</div>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogPortal>
          <DialogOverlay className="fixed inset-0 bg-black/90 z-50" />
          <DialogContent className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-md h-[90vh] bg-black rounded-lg overflow-hidden">
              {/* Close Button */}
              <Button
                onClick={onClose}
                className="absolute top-4 right-4 z-20 bg-black/50 hover:bg-black/70 text-white rounded-full p-2"
              >
                <X className="h-5 w-5" />
              </Button>

              {/* Video Player */}
              <video
                ref={videoRef}
                src={video.videoUrl}
                className="w-full h-full object-cover"
                loop
                muted={isMuted}
                playsInline
                onClick={togglePlayPause}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
              />

              {/* Play/Pause Overlay */}
              {!isPlaying && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Button
                    onClick={togglePlayPause}
                    className="bg-black/50 hover:bg-black/70 text-white rounded-full p-4"
                  >
                    <Play className="h-8 w-8" />
                  </Button>
                </div>
              )}

              {/* Video Info Overlay */}
              <div className="absolute bottom-0 left-0 right-16 text-white p-4 bg-gradient-to-t from-black/80 to-transparent">
                <div className="flex items-center gap-3 mb-2">
                  <img
                    src={video.creator.imageUrl}
                    alt={video.creator.name}
                    className="w-8 h-8 rounded-full border-2 border-white"
                  />
                  <div>
                    <p className="font-semibold text-sm">{video.creator.name}</p>
                    <p className="text-xs text-gray-300">@{video.creator.username}</p>
                  </div>
                </div>
                <h3 className="font-semibold text-base mb-1 line-clamp-2">{video.title}</h3>
                <p className="text-sm text-gray-200 line-clamp-2">{video.description}</p>
              </div>

              {/* Action Buttons */}
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex flex-col gap-3">
                {/* Tip */}
                <div className="flex flex-col items-center">
                  <Button 
                    onClick={handleTipClick}
                    className="bg-green-500 hover:bg-green-600 text-white rounded-full p-3"
                  >
                    <IndianRupee className="h-5 w-5" />
                  </Button>
                  <span className="text-white text-xs mt-1 bg-black/50 px-1 rounded">
                    {video.stats?.tips || 0}
                  </span>
                </div>

                {/* Like */}
                <div className="flex flex-col items-center">
                  <Button
                    onClick={handleVideoLike}
                    className={`rounded-full p-2.5 shadow-lg ${
                      localInteractions.isLiked
                        ? "bg-rose-500 hover:bg-rose-600"
                        : "bg-black/50 hover:bg-black/70"
                    } text-white`}
                  >
                    <Heart
                      className={`h-5 w-5 ${localInteractions.isLiked ? "fill-white" : ""}`}
                    />
                  </Button>
                  <span className="text-white text-xs mt-1 bg-black/50 px-1 rounded">
                    {video.stats?.likes || 0}
                  </span>
                </div>

                {/* Save */}
                <div className="flex flex-col items-center">
                  <Button
                    onClick={handleVideoSave}
                    className={`rounded-full p-2.5 shadow-lg ${
                      localInteractions.isSaved
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-black/50 hover:bg-black/70"
                    } text-white`}
                  >
                    {localInteractions.isSaved ? (
                      <BookmarkCheck className="h-5 w-5" />
                    ) : (
                      <Bookmark className="h-5 w-5" />
                    )}
                  </Button>
                  <span className="text-white text-xs mt-1 bg-black/50 px-1 rounded">
                    {video.stats?.saves || 0}
                  </span>
                </div>

                {/* Share */}
                <div className="flex flex-col items-center">
                  <Button
                    onClick={handleVideoShare}
                    className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5"
                  >
                    <Share className="h-5 w-5" />
                  </Button>
                  <span className="text-white text-xs mt-1 bg-black/50 px-1 rounded">
                    {video.stats?.shares || 0}
                  </span>
                </div>

                {/* Mute Toggle */}
                <Button
                  onClick={toggleMute}
                  className="bg-black/50 hover:bg-black/70 text-white rounded-full p-2.5"
                >
                  {isMuted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </DialogContent>
        </DialogPortal>
      </Dialog>

      {/* Tip Modal */}
      {showTipModal && (
        <Dialog open={showTipModal} onOpenChange={setShowTipModal}>
          <DialogPortal>
            <DialogOverlay className="fixed inset-0 bg-black/80 z-50" />
            <DialogContent className="fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%] bg-dark-2 rounded-lg w-full max-w-sm p-6">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white">Send Tip</h2>
                  <Button
                    onClick={() => setShowTipModal(false)}
                    className="text-gray-400 hover:text-white p-1"
                    variant="ghost"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </div>

                <div className="text-center">
                  <img
                    src={video.creator.imageUrl}
                    alt={video.creator.name}
                    className="w-16 h-16 rounded-full mx-auto mb-2"
                  />
                  <p className="text-white font-medium">{video.creator.name}</p>
                  <p className="text-gray-400 text-sm">@{video.creator.username}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Choose Amount
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {[10, 25, 50, 100, 250, 500].map((amount) => (
                      <Button
                        key={amount}
                        onClick={() => setTipAmount(amount)}
                        className={`${
                          tipAmount === amount
                            ? "bg-primary-500 text-white"
                            : "bg-dark-3 text-gray-300 hover:bg-dark-4"
                        } rounded-lg py-2 text-sm`}
                      >
                        ₹{amount}
                      </Button>
                    ))}
                  </div>
                </div>

                {requiresPaymentPhone(user.emailOrPhone) && (
                  <PaymentPhoneField value={paymentPhone} onChange={setPaymentPhone} dark />
                )}

                <Button
                  onClick={handleSendTip}
                  disabled={isPaymentLoading || !paymentContactFromIdentifier(user.emailOrPhone, paymentPhone).phone}
                  className="w-full bg-green-500 hover:bg-green-600 text-white"
                >
                  {isPaymentLoading ? "Processing..." : `Send ₹${tipAmount} Tip`}
                </Button>
              </div>
            </DialogContent>
          </DialogPortal>
        </Dialog>
      )}
    </>
  );
};

export default BizzVideoModal;
